// src/hooks/useNoteEditor.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNotes } from '../components/contexts/NotesContext';

/**
 * Custom hook for note editing functionality
 * @param {Object} options - Options for the hook
 * @param {Function} options.onImageUpload - Callback for when images are uploaded
 * @returns {Object} - Note editor state and functions
 */
const useNoteEditor = ({ onImageUpload } = {}) => {
  const {
    selectedNoteId,
    getSelectedNote,
    updateNoteContent,
    updateNoteTitle,
    updateNoteCategory,
    refreshNoteImages,
    getNoteImages
  } = useNotes();

  const [note, setNote] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Create ref for image refresh functionality
  const imagesRef = useRef({
    refreshImages: async () => {
      console.log('useNoteEditor: refreshImages called');
      if (!selectedNoteId) return [];
      
      const images = await refreshNoteImages(selectedNoteId);
      onImageUpload?.(images);
      return images;
    },
    images: []
  });

  // Update ref when images change
  useEffect(() => {
    imagesRef.current.images = getNoteImages(selectedNoteId) || [];
  }, [selectedNoteId, getNoteImages]);

  // Load note when selectedNoteId changes
  useEffect(() => {
    if (!selectedNoteId) return;
    
    const loadNote = async () => {
      setIsLoading(true);
      try {
        const currentNote = getSelectedNote();
        if (!currentNote) {
          setIsLoading(false);
          return;
        }

        if (note?.id !== currentNote.id) {
          setNote(currentNote);
        }
      } catch (error) {
        console.error('Error loading note:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadNote();
  }, [selectedNoteId, getSelectedNote, note]);

  // Handle content changes
  const handleNoteChange = useCallback((content) => {
    updateNoteContent(selectedNoteId, content);
    setNote(prev => prev ? { ...prev, note: content } : null);
  }, [selectedNoteId, updateNoteContent]);

  // Handle title changes
  const handleTitleChange = useCallback((title) => {
    updateNoteTitle(selectedNoteId, title);
    setNote(prev => prev ? { ...prev, title } : null);
  }, [selectedNoteId, updateNoteTitle]);

  // Handle category changes
  const handleCategoryChange = useCallback((categoryId) => {
    updateNoteCategory(selectedNoteId, categoryId);
    setNote(prev => prev ? { ...prev, primary_category_id: parseInt(categoryId) } : null);
  }, [selectedNoteId, updateNoteCategory]);

  return {
    note,
    isLoading,
    noteId: selectedNoteId,
    images: getNoteImages(selectedNoteId) || [],
    imagesRef,
    handleNoteChange,
    handleTitleChange,
    handleCategoryChange
  };
};

export default useNoteEditor;